// Enhanced search utility for partial matching
export const enhancedSearch = (searchTerm: string, searchableFields: (string | number | null | undefined)[]): boolean => {
  if (!searchTerm.trim()) return true;
  
  const term = searchTerm.toLowerCase().trim();
  
  return searchableFields.some(field => {
    if (!field) return false;
    
    const fieldStr = field.toString().toLowerCase();
    
    // Basic text matching
    if (fieldStr.includes(term)) return true;
    
    // Special handling for phone numbers with various formats
    // This handles searching for "8765 0123" in "+965-8765-0123"
    const normalizedField = fieldStr.replace(/[\s\-\+\(\)\.\/\\]/g, '');
    const normalizedSearch = term.replace(/[\s\-\+\(\)\.\/\\]/g, '');
    
    if (normalizedField.includes(normalizedSearch)) return true;
    
    // Also try matching with spaces preserved (for "8765 0123" pattern)
    const fieldWithSpaces = fieldStr.replace(/[\-\+\(\)\.\/\\]/g, ' ').replace(/\s+/g, ' ').trim();
    if (fieldWithSpaces.includes(term)) return true;
    
    // Extract only digits for pure number matching
    const fieldNumbers = fieldStr.replace(/[^\d]/g, '');
    const searchNumbers = term.replace(/[^\d]/g, '');
    
    // Partial number matching - minimum 2 digits
    if (searchNumbers.length >= 2 && fieldNumbers.includes(searchNumbers)) {
      return true;
    }
    
    // Handle searches with spaces in numbers (e.g., "8765 0123")
    // Split by space and check if all parts match in sequence
    if (term.includes(' ')) {
      const searchParts = term.split(' ').filter(part => part.length > 0);
      const joinedSearchParts = searchParts.join('');
      
      // Check if the joined parts exist in the field's numbers
      if (fieldNumbers.includes(joinedSearchParts)) {
        return true;
      }
      
      // Also check if parts appear in sequence with any separators
      const searchPattern = searchParts.join('[^\\d]*');
      const regex = new RegExp(searchPattern);
      if (regex.test(fieldStr)) {
        return true;
      }
    }
    
    // Alphanumeric matching for passport numbers (e.g., F3456789)
    const fieldAlphaNum = fieldStr.replace(/[^\w]/g, '');
    const searchAlphaNum = term.replace(/[^\w]/g, '');
    
    if (searchAlphaNum.length >= 2 && fieldAlphaNum.includes(searchAlphaNum)) {
      return true;
    }
    
    // Word-based matching
    const fieldWords = fieldStr.split(/[\s\-\+\(\)\.\,]+/);
    const searchWords = term.split(/[\s\-\+\(\)\.\,]+/);
    
    return searchWords.every(searchWord => 
      fieldWords.some(fieldWord => 
        fieldWord.includes(searchWord) || fieldWord.startsWith(searchWord)
      )
    );
  });
};

// Enhanced substring matching function
const isSubstringMatch = (text: string, search: string): boolean => {
  if (search.length < 2) return false;
  
  // Check every possible position in the text
  for (let i = 0; i <= text.length - search.length; i++) {
    if (text.substring(i, i + search.length) === search) {
      return true;
    }
  }
  
  // Also check without special characters
  const cleanText = text.replace(/[^\w]/g, '');
  const cleanSearch = search.replace(/[^\w]/g, '');
  
  if (cleanSearch.length >= 2) {
    for (let i = 0; i <= cleanText.length - cleanSearch.length; i++) {
      if (cleanText.substring(i, i + cleanSearch.length) === cleanSearch) {
        return true;
      }
    }
  }
  
  return false;
};

// Simple fuzzy matching for typos and close matches
const isFuzzyMatch = (text: string, search: string): boolean => {
  if (search.length < 3) return false; // Only apply fuzzy for longer searches
  
  // Check if most characters are present in order
  let textIndex = 0;
  let matchCount = 0;
  
  for (let i = 0; i < search.length; i++) {
    const char = search[i];
    const foundIndex = text.indexOf(char, textIndex);
    
    if (foundIndex !== -1) {
      textIndex = foundIndex + 1;
      matchCount++;
    }
  }
  
  // Return true if at least 80% of characters match in order
  return matchCount / search.length >= 0.8;
};

// Helper function to filter array of objects with enhanced search
export const filterWithEnhancedSearch = <T>(
  items: T[], 
  searchTerm: string, 
  getSearchFields: (item: T) => (string | number | null | undefined)[]
): T[] => {
  if (!searchTerm.trim()) return items;
  
  return items.filter(item => 
    enhancedSearch(searchTerm, getSearchFields(item))
  );
};